/**
 * Project Configuration Page
 *
 * Comprehensive configuration interface for projects including:
 * - Project details (read-only summary)
 * - Repository connection management
 * - Standards configuration
 * - Compliance requirements
 * - Team members
 * - Budget and cost tracking
 *
 * Navigation: /projects/:projectId/configure
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Tabs } from 'flowbite-react';
import {
  HiCog,
  HiArrowLeft,
  HiDatabase,
  HiDocumentText,
  HiShieldCheck,
  HiUsers,
  HiCurrencyDollar
} from 'react-icons/hi';

import { getProject, type Project } from '../api/projects';
import ProjectRepositoryLink from '../components/ProjectRepositoryLink';
import ProjectStandardsSelector from '../components/ProjectStandardsSelector';
import ProjectComplianceConfig from '../components/ProjectComplianceConfig';
import TeamManagementTab from '../components/TeamManagementTab';
import BudgetDashboardTab from '../components/BudgetDashboardTab';

export const ProjectConfigurationPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [clientId, setClientId] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);

  // Load client and company context from localStorage
  useEffect(() => {
    const savedClientId = localStorage.getItem('selectedClientId');
    const savedCompanyId = localStorage.getItem('selectedCompanyId');

    if (savedClientId) setClientId(savedClientId);
    if (savedCompanyId) setCompanyId(savedCompanyId);
  }, []);

  // Load project details
  useEffect(() => {
    const loadProject = async () => {
      if (!clientId || !companyId || !projectId) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await getProject(clientId, companyId, projectId);

        if (!response.success) {
          throw new Error(response.message || 'Failed to load project');
        }

        setProject(response.data.Records[0]);
      } catch (err) {
        console.error('[ProjectConfigurationPage] Error loading project:', err);
        setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [clientId, companyId, projectId]);

  const handleBack = useCallback(() => {
    navigate('/projects');
  }, [navigate]);

  const handleRepositoryLinked = useCallback(() => {
    // Refresh project data to get updated repository link
    if (clientId && companyId && projectId) {
      getProject(clientId, companyId, projectId).then((response) => {
        if (response.success) {
          setProject(response.data.Records[0]);
        }
      });
    }
  }, [clientId, companyId, projectId]);

  const handleStandardsUpdated = useCallback(() => {
    // Refresh project data
    if (clientId && companyId && projectId) {
      getProject(clientId, companyId, projectId).then((response) => {
        if (response.success) {
          setProject(response.data.Records[0]);
        }
      });
    }
  }, [clientId, companyId, projectId]);

  const handleComplianceUpdated = useCallback(() => {
    // Refresh project data
    if (clientId && companyId && projectId) {
      getProject(clientId, companyId, projectId).then((response) => {
        if (response.success) {
          setProject(response.data.Records[0]);
        }
      });
    }
  }, [clientId, companyId, projectId]);

  if (!clientId || !companyId) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <Alert color="warning">
            <div>
              <p className="font-semibold">No Client or Company Selected</p>
              <p className="text-sm mt-1">
                Please select a client and company from the dashboard.
              </p>
            </div>
          </Alert>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center min-h-screen">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <Alert color="failure">
            <p className="font-semibold">Error Loading Project</p>
            <p className="text-sm mt-1">{error || 'Project not found'}</p>
          </Alert>
          <div className="mt-4">
            <Button color="gray" onClick={handleBack}>
              <HiArrowLeft className="w-4 h-4 mr-2" />
              Back to Projects
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button color="light" onClick={handleBack}>
            <HiArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center space-x-2">
              <HiCog className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">Project Configuration</h1>
            </div>
            <p className="text-gray-600 mt-1">
              Configure repository connections, standards, compliance, and team settings
            </p>
          </div>
        </div>
      </div>

      {/* Project Summary Card */}
      <Card>
        <div className="border-b border-gray-200 pb-4">
          <h2 className="text-xl font-semibold text-gray-900">{project.Project_Name}</h2>
          <p className="text-sm text-gray-600 mt-1">
            <span className="font-medium">Project ID:</span> {project.Project_ID}
          </p>
          {project.Project_Description && (
            <p className="text-sm text-gray-700 mt-2">{project.Project_Description}</p>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
          <div>
            <p className="text-xs text-gray-500">Status</p>
            <p className="text-sm font-medium capitalize">{project.Project_Status}</p>
          </div>
          {project.Budget_USD && (
            <div>
              <p className="text-xs text-gray-500">Budget</p>
              <p className="text-sm font-medium">
                ${project.Budget_USD.toLocaleString()} {project.Budget_Period && `(${project.Budget_Period})`}
              </p>
            </div>
          )}
          {project.Owner_Email && (
            <div>
              <p className="text-xs text-gray-500">Owner</p>
              <p className="text-sm font-medium">{project.Owner_Email}</p>
            </div>
          )}
          {project.Cost_Center && (
            <div>
              <p className="text-xs text-gray-500">Cost Center</p>
              <p className="text-sm font-medium">{project.Cost_Center}</p>
            </div>
          )}
        </div>
      </Card>

      {/* Configuration Tabs */}
      <Card>
        <Tabs
          aria-label="Project configuration tabs"
          onActiveTabChange={(tab: number) => setActiveTab(tab)}
        >
          {/* Repository Tab */}
          <Tabs.Item
            active={activeTab === 0}
            title="Repository"
            icon={HiDatabase}
          >
            <div className="py-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Repository Connection
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Connect a code repository to this project for automated scanning,
                documentation generation, and CI/CD integration.
              </p>

              <ProjectRepositoryLink
                clientId={clientId}
                companyId={companyId}
                projectId={projectId!}
                onSuccess={handleRepositoryLinked}
              />
            </div>
          </Tabs.Item>

          {/* Standards Tab */}
          <Tabs.Item
            active={activeTab === 1}
            title="Standards"
            icon={HiDocumentText}
          >
            <div className="py-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Development Standards
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Select the development standards and best practices that apply to this project.
                These will be used for code generation, validation, and documentation.
              </p>

              <ProjectStandardsSelector
                clientId={clientId}
                companyId={companyId}
                projectId={projectId!}
                onUpdate={handleStandardsUpdated}
              />
            </div>
          </Tabs.Item>

          {/* Compliance Tab */}
          <Tabs.Item
            active={activeTab === 2}
            title="Compliance"
            icon={HiShieldCheck}
          >
            <div className="py-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Compliance Requirements
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Configure compliance requirements for this project. These settings will
                affect code generation, security scanning, and documentation requirements.
              </p>

              <ProjectComplianceConfig
                clientId={clientId}
                companyId={companyId}
                projectId={projectId!}
                onUpdate={handleComplianceUpdated}
              />
            </div>
          </Tabs.Item>

          {/* Team Tab */}
          <Tabs.Item
            active={activeTab === 3}
            title="Team"
            icon={HiUsers}
          >
            <div className="py-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Team Members
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Manage team member access and roles for this project. Assign developers, leads,
                reviewers, and observers with specific project permissions.
              </p>

              <TeamManagementTab
                clientId={clientId}
                companyId={companyId}
                projectId={projectId!}
              />
            </div>
          </Tabs.Item>

          {/* Budget & Costs Tab */}
          <Tabs.Item
            active={activeTab === 4}
            title="Budget & Costs"
            icon={HiCurrencyDollar}
          >
            <div className="py-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Budget Tracking & Cost Analysis
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Monitor project costs, track budget utilization, and analyze spending patterns.
              </p>

              <BudgetDashboardTab
                clientId={clientId}
                companyId={companyId}
                projectId={projectId!}
              />
            </div>
          </Tabs.Item>
        </Tabs>
      </Card>
    </div>
  );
};

export default ProjectConfigurationPage;
